import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { getWebSocketUrl } from "../../../api/websocket";
import { createWsWithReconnect } from "../../../utils/wsWithReconnect";
import {
  zaloStatusChanged,
  zaloStatusSnapshotReceived,
  zaloStatusSubscriptionConnected,
  zaloStatusSubscriptionDisconnected,
} from "../slices/zaloStatusActions";

/**
 * Persistent WS subscription for per-account shop login status.
 *
 * Opens its own connection (separate from ChatPage's `listen_message` socket),
 * sends `account_subscribe` on open, and dispatches snapshot/change actions
 * into the `zaloStatus` Redux slice. Reconnects with exponential backoff via
 * `createWsWithReconnect`; every reconnect re-sends `account_subscribe` so the
 * server always returns a fresh authoritative snapshot.
 *
 * @param {Object} args
 * @param {number|string} args.accountId - current user's accountId
 * @param {string} args.staffId - current user's staff id (server-side staffs._id)
 * @param {boolean} args.enabled - gate until auth.user is populated
 */
export const useAccountShopStatusSubscription = ({
  accountId,
  staffId,
  enabled = true,
}) => {
  const dispatch = useDispatch();
  const connRef = useRef(null);

  useEffect(() => {
    if (!enabled || accountId == null || !staffId) return undefined;

    const conn = createWsWithReconnect(getWebSocketUrl(), {
      onOpen: (_ev, socket) => {
        try {
          socket.send(
            JSON.stringify({
              type: "account_subscribe",
              accountId,
              staffId,
            }),
          );
        } catch {}
        dispatch(zaloStatusSubscriptionConnected({ accountId, staffId }));
      },
      onMessage: (ev) => {
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (!msg || typeof msg !== "object") return;

        if (msg.type === "shop_status_snapshot") {
          dispatch(zaloStatusSnapshotReceived({ shops: msg.shops || [] }));
        } else if (msg.type === "shop_status_changed") {
          if (msg.shop) {
            dispatch(zaloStatusChanged({ shop: msg.shop }));
          }
        } else if (msg.type === "account_subscribe_error") {
          console.warn(
            `[ZaloStatusSub] account_subscribe rejected: ${msg.reason}`,
          );
        }
        // Other message types (welcome, pong, listen_message responses) are
        // not relevant to this single-purpose socket.
      },
      onError: (err) => {
        console.error("[ZaloStatusSub] WebSocket error:", err);
      },
      onClose: () => {
        dispatch(zaloStatusSubscriptionDisconnected());
      },
      onReconnecting: (attempt, delay) => {
        console.log(
          `[ZaloStatusSub] reconnecting in ${delay}ms (attempt ${attempt})`,
        );
      },
    });

    connRef.current = conn;

    return () => {
      const socket = connRef.current?.socket;
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(
            JSON.stringify({ type: "account_unsubscribe", accountId }),
          );
        } catch {}
      }
      connRef.current?.close();
      connRef.current = null;
    };
  }, [accountId, staffId, enabled, dispatch]);
};

export default useAccountShopStatusSubscription;
